import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import SearchableDropdown from '../../../components/common/SearchableDropdown';

const SalesOrder = () => {
  const [formData, setFormData] = useState({
    order_date: '',
    warehouse_id: '',
    broker_id: '',
    buyer_id: '',
    quantity_mt: '',
    sale_price_per_mt: '',
    cost_basis_unit_inr: '',
    
    remarks: '',
    purchase_contract_id: '',
    cma_id: ''
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [availableStock, setAvailableStock] = useState(null);
  const [quantityError, setQuantityError] = useState('');

  const [cmaList, setCmaList] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [domesticBuyers, setBuyers] = useState([]);
  const [purchaseContracts, setPurchaseContracts] = useState([]);
  const navigate = useNavigate();

  // Fetch dropdown data
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        // Fetch sale brokers
        const brokerRes = await fetch(`${API_BASE_URL}/api/masters/sale-broker-get`, {
          credentials: 'include'
        });
        if (brokerRes.ok) {
          const brokerData = await brokerRes.json();
          setBrokers(brokerData.data || []);
        }

        // Fetch CMA List
        const cmaRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-cma-list`, {
          credentials: 'include'
        });
        if (cmaRes.ok) {
          const cmaData = await cmaRes.json();
          setCmaList(cmaData.data || []);
        }

        // Fetch purchase contracts
        const purchaseContractRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-purchase-contract`, {
          credentials: 'include'
        });
        if (purchaseContractRes.ok) {
          const purchaseContractData = await purchaseContractRes.json();
          setPurchaseContracts(purchaseContractData.data || []);
        }

        // Fetch domestic buyers
        const domesticBuyerRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-domestic-buyers`, {
          credentials: 'include'
        });
        if (domesticBuyerRes.ok) {
          const domesticBuyerData = await domesticBuyerRes.json();
          setBuyers(domesticBuyerData.data || []);
        }

      } catch (error) {
        console.error('Error fetching dropdown data:', error);
      }
    };

    fetchDropdownData();
  }, []);

  // Fetch warehouses when CMA changes
  useEffect(() => {
    const fetchWarehouses = async () => {
      if (formData.cma_id) {
        try {
          const res = await fetch(`${API_BASE_URL}/api/masters/dropdown-warehouse-list-by-cma?cma_id=${formData.cma_id}`, {
            credentials: 'include'
          });
          if (res.ok) {
            const data = await res.json();
            setWarehouses(data.data || []);
          }
        } catch (error) {
          console.error('Error fetching warehouses:', error);
        }
      } else {
        setWarehouses([]);
      }
    };
    fetchWarehouses();
  }, [formData.cma_id]);

  useEffect(() => {
    const fetchStock = async () => {
      if (formData.purchase_contract_id && formData.warehouse_id) {
        try {
          const res = await fetch(`${API_BASE_URL}/api/sales/available-stock?purchase_contract_id=${formData.purchase_contract_id}&warehouse_id=${formData.warehouse_id}`, {
            credentials: 'include'
          });
          if (res.ok) {
            const data = await res.json();
            setAvailableStock(data.data.available_stock);
            validateQuantity(formData.quantity_mt, data.data.available_stock);
          }
        } catch (error) {
          console.error("Error fetching stock:", error);
        }
      } else {
        setAvailableStock(null);
      }
    };
    fetchStock();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.purchase_contract_id, formData.warehouse_id]);

  const validateQuantity = (quantity, maxStock) => {
    const stockToCheck = maxStock !== undefined ? maxStock : availableStock;
    if (stockToCheck !== null && quantity && parseFloat(quantity) > stockToCheck) {
      setQuantityError(`Quantity cannot exceed available stock (${stockToCheck} MT)`);
    } else {
      setQuantityError('');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (name === 'quantity_mt') {
      validateQuantity(value);
    }
    
    // Clear any existing error message when user starts typing
    if (message && message.type === 'error' && message.text.includes('Quantity')) {
      setMessage(null);
    }
  };

  const handleDropdownChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    
    // Clear warehouse if CMA changes
    if (fieldName === 'cma_id') {
      setFormData(prev => ({ ...prev, warehouse_id: '' }));
      setAvailableStock(null);
      setQuantityError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (quantityError) {
      setMessage({ type: 'error', text: 'Please fix the quantity error before submitting' });
      return;
    }
    setLoading(true);
    setMessage(null);

    // Note: Since sales contract is gone, we might not have a remaining quantity check 
    // strictly enforced here unless purchase contract tracks remaining_qty for sales.
    // Assuming we just skip the quantity validation for now.


    // Convert empty string date fields to null
    const processedFormData = { ...formData };
    ['order_date'].forEach(field => {
      if (processedFormData[field] === '') {
        processedFormData[field] = null;
      }
    });

    try {
      const res = await fetch(`${API_BASE_URL}/api/sales/sales-order-post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(processedFormData),
      });
      
      const data = await res.json();
      if (res.ok) {
        setMessage({ type: 'success', text: 'Sales Order Created Successfully!' });
        // Reset form
        setFormData({
          order_date: '',
          warehouse_id: '',
          broker_id: '',
          buyer_id: '',
          quantity_mt: '',
          sale_price_per_mt: '',
          cost_basis_unit_inr: '',
         
          remarks: '',
          purchase_contract_id: '',
          cma_id: ''
        });


      } else {
        setMessage({ type: 'error', text: data.error || 'Something went wrong' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Server Error' });
    }
    setLoading(false);
  };

  return (
    <div className="max-w-8xl mx-auto p-0 bg-white">
      <div className="flex justify-between items-center border-b border-gray-100 bg-gray-50 px-3 py-1.5">
        <h2 className="text-[15px] font-bold mb-0 text-left">Sales Order Entry</h2>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-[#15803d] border border-[#15803d] px-2 py-0.5 rounded text-[12px] font-medium hover:bg-[#f0fdf4]"
        >
          <FaArrowLeft />
          Back
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-0">
        <div className="px-3 pt-4 pb-2">
          {/* Purchase Contract and Purchase No at the top */}
          <div className="mb-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-[12px] font-medium mb-1">Purchase Contract No</label>
              <SearchableDropdown
                options={purchaseContracts}
                value={formData.purchase_contract_id}
                onChange={(value) => handleDropdownChange('purchase_contract_id', value)}
                placeholder="Select Purchase Contract"
                displayKey="purchase_contract_no"
                valueKey="id"
                required
              />
            </div>
        <div>
              <label className="block text-[12px] font-medium mb-1">Order Date</label>
              <input 
                type="date" 
                name="order_date" 
                value={formData.order_date} 
                onChange={handleChange} 
                className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none" 
                required 
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
           

            <div>
              <label className="block text-[12px] font-medium mb-1">CMA</label>
              <SearchableDropdown
                options={cmaList}
                value={formData.cma_id}
                onChange={(value) => handleDropdownChange('cma_id', value)}
                placeholder="Select CMA"
                displayKey="name"
                valueKey="cma_id"
                required
              />
            </div>

            <div>
              <label className="block text-[12px] font-medium mb-1">Warehouse</label>
              <SearchableDropdown
                options={warehouses}
                value={formData.warehouse_id}
                onChange={(value) => handleDropdownChange('warehouse_id', value)}
                placeholder={formData.cma_id ? "Select Warehouse" : "Select CMA first"}
                displayKey="name"
                valueKey="warehouse_id"
                required
              />
            </div>

            <div>
              <label className="block text-[12px] font-medium mb-1">Broker</label>
              <SearchableDropdown
                options={brokers}
                value={formData.broker_id}
                onChange={(value) => handleDropdownChange('broker_id', value)}
                placeholder="Select Broker"
                displayKey="name"
                valueKey="broker_id"
                required
              />
            </div>

            <div>
              <label className="block text-[12px] font-medium mb-1">Buyer</label>
              <SearchableDropdown
                options={domesticBuyers}
                value={formData.buyer_id}
                onChange={(value) => handleDropdownChange('buyer_id', value)}
                placeholder="Select Buyer"
                displayKey="name"
                valueKey="buyer_id"
                required
              />
            </div>

            <div>
              <label className="block text-[12px] font-medium mb-1">
                Quantity (MT)
              </label>
              <input 
                type="number" 
                step="0.001"
                name="quantity_mt" 
                value={formData.quantity_mt} 
                onChange={handleChange} 
                className={`w-full border px-3 py-1.5 text-[13px] bg-white focus:outline-none rounded-none ${
                  quantityError ? 'border-red-500' : 'border-gray-300 focus:border-[#15803d]'
                }`}
                required 
              />
              {quantityError && (
                <div className="text-red-500 text-[12px] mt-1">{quantityError}</div>
              )}
              {availableStock !== null && !quantityError && (
                <div className="text-gray-500 text-[12px] mt-1">
                  Available stock: {availableStock} MT
                </div>
              )}
            </div>

            <div>
              <label className="block text-[12px] font-medium mb-1">Sale Price/MT</label>
              <input 
                type="number" 
                step="0.01"
                name="sale_price_per_mt" 
                value={formData.sale_price_per_mt} 
                onChange={handleChange} 
                className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none" 
                required 
              />
            </div>

            <div>
              <label className="block text-[12px] font-medium mb-1">Cost Basis Unit (INR)</label>
              <input 
                type="number" 
                step="0.01"
                name="cost_basis_unit_inr" 
                value={formData.cost_basis_unit_inr} 
                onChange={handleChange} 
                className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none" 
                required 
              />
            </div>




          </div>

          <div className="mt-3">
            <label className="block text-[12px] font-medium mb-1">Remarks</label>
            <textarea 
              name="remarks" 
              value={formData.remarks} 
              onChange={handleChange} 
              className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none resize-none" 
              rows={3}
            ></textarea>
          </div>
        </div>

        <div className="px-3 pt-2 pb-4">
          <button
            type="submit"
            className="w-auto px-3 py-1.5 text-[13px] bg-[#15803d] text-white font-semibold tracking-wide rounded border border-[#15803d] hover:bg-[#166534] transition disabled:opacity-60"
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Sales Order'}
          </button>
          {message && (
            <div className={`mt-2 text-center text-[13px] ${message.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
              {message.text}
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

export default SalesOrder;